import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";
import ScrollToTop from "./components/ScrollToTop";
import OAuthCallback from "./components/OAuthCallback";
import { Toaster } from "./components/ui/sonner";
import NotFound from "./pages/NotFound";
import { UnauthorizedListener } from "./components/routing/guards";
import PublicLayout from "./layouts/PublicLayout";
import PortalLayout from "./layouts/PortalLayout";
import { publicRoutes, patronFullscreenRoutes, ownHeaderRoutes } from "./routes/public";
import { businessRoutes } from "./routes/business";
import { venuePublicRoutes, venuePortalRoutes } from "./routes/venue";
import { adminRoutes } from "./routes/admin";
import { redactUrl } from "./lib/urlPrivacy";

// GTM Page View Tracker Component
function GTMPageTracker() {
  const location = useLocation();

  useEffect(() => {
    // Push page view to GTM dataLayer on route change
    // Row 112: analytics only after the visitor accepted cookies (ConsentBanner)
    // URL without `t` / `token` (lib/urlPrivacy): this runs before the wallet page strips its link token
    let consent = null;
    try { consent = localStorage.getItem('cookieConsent'); } catch {}
    if (window.dataLayer && consent === 'accepted') {
      window.dataLayer.push({
        event: 'pageview',
        page: {
          path: location.pathname,
          title: document.title,
          url: redactUrl(window.location.href)
        }
      });
    }
  }, [location]);

  return null;
}

// Auth Modal Manager Component
function AuthModalManager() {
  const location = useLocation();
  const { showAuthModal, authMode, closeAuthModal } = useAuth();
  
  // Don't show customer auth modal on admin pages
  const isAdminRoute = location.pathname.startsWith('/admin');
  if (isAdminRoute) {
    return null;
  }
  
  return (
    <AuthModal
      isOpen={showAuthModal}
      onClose={closeAuthModal}
      initialMode={authMode}
    />
  );
}

/**
 * App assembly. Chrome is decided by layout membership, not by path lists:
 * a route under <PublicLayout> gets Header/Footer/ConsentBanner/MobileBottomNav,
 * a route under <PortalLayout> gets none. Routes live in src/routes/*.jsx.
 */
function App() {
  return (
    <div className="App">
      <ScrollToTop />
      <AuthProvider>
        <GTMPageTracker />
        <UnauthorizedListener />
        <Routes>
          <Route element={<PublicLayout />}>
            {publicRoutes()}
            {venuePublicRoutes()}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<PortalLayout />}>
            {patronFullscreenRoutes()}
            {venuePortalRoutes()}
            {businessRoutes()}
            {adminRoutes()}
          </Route>
          <Route element={<PortalLayout bottomNav />}>
            {ownHeaderRoutes()}
          </Route>
        </Routes>
        <AuthModalManager />
        <OAuthCallback />
        <Toaster />
      </AuthProvider>
    </div>
  );
}

function AppWrapper() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default AppWrapper;
